import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import {
  CHART_COLORS,
  monthNamesFull,
  monthNamesShort
} from './TrendsChart.constant';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TrendsChartProps {
  aiSystemId: string;
}

export function TrendsChart({ aiSystemId }: TrendsChartProps) {
  const { trends, trendsLoading } = useSelector(
    (state: RootState) => state.systemLogging
  );

  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: any[];
    yAxisMax?: number;
  }>({
    labels: [],
    datasets: []
  });
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!trends) return;

    try {
      const currentYear = new Date().getFullYear();
      const dataMap = new Map(trends.data.map((item) => [item.month, item]));

      const minorData: (number | null)[] = [];
      const moderateData: (number | null)[] = [];
      const majorData: (number | null)[] = [];
      const minorPointRadius: number[] = [];
      const moderatePointRadius: number[] = [];
      const majorPointRadius: number[] = [];

      for (let i = 0; i < 12; i++) {
        const fullMonthKey = `${monthNamesFull[i]} ${currentYear}`;
        const monthData = dataMap.get(fullMonthKey);

        if (monthData) {
          minorData.push(monthData.minor);
          moderateData.push(monthData.moderate);
          majorData.push(monthData.major);
          minorPointRadius.push(4);
          moderatePointRadius.push(4);
          majorPointRadius.push(4);
        } else {
          minorData.push(null);
          moderateData.push(null);
          majorData.push(null);
          minorPointRadius.push(0);
          moderatePointRadius.push(0);
          majorPointRadius.push(0);
        }
      }

      const allValues = [...minorData, ...moderateData, ...majorData].filter(
        (v): v is number => v !== null
      );
      const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
      const hasAnyData = allValues.some((value) => value > 0);
      setHasData(hasAnyData);

      // Round up to next even number (step is 2)
      const yAxisMax = hasAnyData ? Math.ceil((maxValue + 2) / 2) * 2 : 50;

      setChartData({
        labels: monthNamesShort,
        datasets: [
          {
            label: 'Minor',
            data: hasAnyData ? minorData : [],
            borderColor: CHART_COLORS.minor,
            backgroundColor: CHART_COLORS.minor,
            tension: 0,
            pointRadius: hasAnyData ? minorPointRadius : 0,
            pointHoverRadius: hasAnyData ? 6 : 0,
            borderWidth: hasAnyData ? 2 : 0,
            spanGaps: false
          },
          {
            label: 'Moderate',
            data: hasAnyData ? moderateData : [],
            borderColor: CHART_COLORS.moderate,
            backgroundColor: CHART_COLORS.moderate,
            tension: 0,
            pointRadius: hasAnyData ? moderatePointRadius : 0,
            pointHoverRadius: hasAnyData ? 6 : 0,
            borderWidth: hasAnyData ? 2 : 0,
            spanGaps: false
          },
          {
            label: 'Major',
            data: hasAnyData ? majorData : [],
            borderColor: CHART_COLORS.major,
            backgroundColor: CHART_COLORS.major,
            tension: 0,
            pointRadius: hasAnyData ? majorPointRadius : 0,
            pointHoverRadius: hasAnyData ? 6 : 0,
            borderWidth: hasAnyData ? 2 : 0,
            spanGaps: false
          }
        ],
        yAxisMax
      });
    } catch (error) {
      console.error('Failed to process trends data:', error);
      setHasData(false);
    }
  }, [trends]);

  const data = chartData;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: hasData
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: chartData.yAxisMax,
        ticks: {
          stepSize: !hasData ? 10 : undefined,
          font: {
            family: 'Inter',
            size: 11
          }
        },
        grid: {
          display: true,
          color: CHART_COLORS.grid,
          drawBorder: false
        },
        border: {
          display: false
        }
      },
      x: {
        ticks: {
          font: {
            family: 'Inter',
            size: 11
          }
        },
        grid: {
          display: true,
          color: CHART_COLORS.grid,
          drawBorder: false
        },
        border: {
          display: false
        }
      }
    },
    ...(!hasData && { events: [] })
  };

  return (
    <div className="flex flex-col rounded-lg bg-white px-4 py-6 gap-3 ring-1 ring-[#DADADD] h-full">
      <span className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-[#767681] text-left">
        TRENDS
      </span>
      <div className="flex flex-col">
        <div className="h-[252px] relative">
          {trendsLoading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-gray-500">Loading trends data...</span>
            </div>
          ) : (
            <>
              <Line data={data} options={options} />
              {!hasData && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="font-inter font-medium text-sm leading-5 text-center text-[#111827] whitespace-nowrap">
                    No data available
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        {hasData ? (
          <div className="flex gap-3 justify-center flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-6 h-2 bg-cyan-200 rounded-sm"></div>
              <span className="font-inter text-xs text-gray-700">Minor</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-2 bg-orange-400 rounded-sm"></div>
              <span className="font-inter text-xs text-gray-700">Moderate</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-2 bg-rose-400 rounded-sm"></div>
              <span className="font-inter text-xs text-gray-700">Major</span>
            </div>
          </div>
        ) : (
          <div className="h-[16px]"></div>
        )}
      </div>
    </div>
  );
}
